import { useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import ReactCanvasConfetti from 'react-canvas-confetti'

interface ConfettiRef {
  fire: () => void
}

interface ConfettiProps {
  trigger?: boolean
  onComplete?: () => void
}

export const Confetti = forwardRef<ConfettiRef, ConfettiProps>(({ trigger, onComplete }, ref) => {
  const refAnimationInstance = useRef<unknown>(null)

  const getInstance = useCallback((instance: { confetti: unknown }) => {
    refAnimationInstance.current = instance.confetti
  }, [])

  const makeShot = useCallback((particleRatio: number, opts: Record<string, unknown>) => {
    const confetti = refAnimationInstance.current as (opts: Record<string, unknown>) => void
    if (confetti) {
      confetti({
        ...opts,
        origin: { y: 0.7 },
        particleCount: Math.floor(200 * particleRatio),
      })
    }
  }, [])

  const fire = useCallback(() => {
    makeShot(0.25, {
      spread: 26,
      startVelocity: 55,
    })

    makeShot(0.2, {
      spread: 60,
    })

    makeShot(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    })

    makeShot(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    })

    makeShot(0.1, {
      spread: 120,
      startVelocity: 45,
    })

    // 애니메이션 완료 후 콜백 실행
    setTimeout(() => {
      onComplete?.()
    }, 3000)
  }, [makeShot, onComplete])

  // ref를 통해 외부에서 fire 함수 호출 가능
  useImperativeHandle(
    ref,
    () => ({
      fire,
    }),
    [fire]
  )

  // trigger prop이 true가 되면 confetti 실행
  useEffect(() => {
    if (trigger) {
      fire()
    }
  }, [trigger, fire])

  return (
    <ReactCanvasConfetti
      onInit={getInstance}
      style={{
        position: 'absolute',
        pointerEvents: 'none',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 1000,
      }}
    />
  )
})

Confetti.displayName = 'Confetti'

export type { ConfettiRef }
